import { useCallback, useSyncExternalStore } from 'react';

export interface CookiePreferences {
  /** Strictly necessary — always true, cannot be disabled */
  essential: true;
  /** Analytics, performance, personalization */
  analytics: boolean;
  /** Marketing / third-party tracking */
  marketing: boolean;
  /** Timestamp of the user's decision (ISO string) */
  decidedAt: string;
}

const STORAGE_KEY = 'arcoll-tech:cookie-consent:v1';

type Listener = () => void;
const listeners = new Set<Listener>();

function emit(): void {
  listeners.forEach((l) => l());
}

/**
 * useSyncExternalStore requires getSnapshot to return the SAME object
 * reference when the underlying store has not changed — otherwise React
 * sees a fresh reference each render and enters an infinite loop.
 *
 * We key the cache on the raw localStorage string so identical reads reuse
 * the same parsed object. A sentinel Symbol distinct from any real string
 * or null guarantees the first read falls through the parse path.
 */
const UNREAD = Symbol('unread-consent');
let cachedRaw: string | null | typeof UNREAD = UNREAD;
let cachedSnapshot: CookiePreferences | null = null;

function parseRaw(raw: string | null): CookiePreferences | null {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const obj = parsed as Partial<CookiePreferences>;
    if (typeof obj.analytics !== 'boolean') return null;
    if (typeof obj.marketing !== 'boolean') return null;
    if (typeof obj.decidedAt !== 'string') return null;
    return {
      essential: true,
      analytics: obj.analytics,
      marketing: obj.marketing,
      decidedAt: obj.decidedAt,
    };
  } catch {
    return null;
  }
}

function readFromStorage(): CookiePreferences | null {
  if (typeof window === 'undefined') return null;
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (raw === cachedRaw) return cachedSnapshot;
  cachedRaw = raw;
  cachedSnapshot = parseRaw(raw);
  return cachedSnapshot;
}

function writeToStorage(prefs: CookiePreferences): void {
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  // Invalidate the cache so the next read re-parses.
  cachedRaw = UNREAD;
  emit();
}

export function acceptAll(): CookiePreferences {
  const prefs: CookiePreferences = {
    essential: true,
    analytics: true,
    marketing: true,
    decidedAt: new Date().toISOString(),
  };
  writeToStorage(prefs);
  return prefs;
}

export function rejectOptional(): CookiePreferences {
  const prefs: CookiePreferences = {
    essential: true,
    analytics: false,
    marketing: false,
    decidedAt: new Date().toISOString(),
  };
  writeToStorage(prefs);
  return prefs;
}

interface SaveCustomInput {
  analytics: boolean;
  marketing: boolean;
}

export function saveCustomPreferences(partial: SaveCustomInput): CookiePreferences {
  const prefs: CookiePreferences = {
    essential: true,
    analytics: partial.analytics,
    marketing: partial.marketing,
    decidedAt: new Date().toISOString(),
  };
  writeToStorage(prefs);
  return prefs;
}

export function resetConsent(): void {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(STORAGE_KEY);
  cachedRaw = UNREAD;
  emit();
}

/**
 * Reactive consent hook. Returns null when the user has not decided yet,
 * or the stored CookiePreferences otherwise.
 */
export function useCookieConsent(): CookiePreferences | null {
  const subscribe = useCallback((onChange: () => void) => {
    listeners.add(onChange);
    const onStorage = (event: StorageEvent) => {
      if (event.key === STORAGE_KEY) {
        // Another tab wrote — bust our cache before re-reading
        cachedRaw = UNREAD;
        onChange();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => {
      listeners.delete(onChange);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const getServerSnapshot = (): CookiePreferences | null => null;

  return useSyncExternalStore(subscribe, readFromStorage, getServerSnapshot);
}
